import { useMemo } from "react";

import { paths } from "src/routes/paths";
import { useTranslate } from "src/locales";
import { usePathname, useSearchParams } from "src/routes/hooks";
import { usePermissions } from "src/auth/hooks/use-permissions";

import SvgColor from "src/components/svg-color";

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
);

const ICONS = {
  job: icon("ic_job"),
  blog: icon("ic_blog"),
  chat: icon("ic_chat"),
  mail: icon("ic_mail"),
  user: icon("ic_user"),
  file: icon("ic_file"),
  lock: icon("ic_lock"),
  tour: icon("ic_tour"),
  order: icon("ic_order"),
  label: icon("ic_label"),
  blank: icon("ic_blank"),
  kanban: icon("ic_kanban"),
  folder: icon("ic_folder"),
  banking: icon("ic_banking"),
  booking: icon("ic_booking"),
  invoice: icon("ic_invoice"),
  product: icon("ic_product"),
  calendar: icon("ic_calendar"),
  disabled: icon("ic_disabled"),
  external: icon("ic_external"),
  menuItem: icon("ic_menu_item"),
  ecommerce: icon("ic_ecommerce"),
  analytics: icon("ic_analytics"),
  dashboard: icon("ic_dashboard"),
  document: icon("ic_file"),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslate();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasDuplicate = searchParams.get("duplicate") !== null;

  const { hasMotorFeature, hasAppFeature, checkGenericModule } =
    usePermissions();

  const data = useMemo(() => {
    const navSections = [];

    // ===================================================================
    // SEÇÃO MOTOR (Geração de Pontos)
    // ===================================================================
    // Subseção: Motor
    if (hasMotorFeature()) {
      const motorItems = [];

      // Subseção: Geração de pontos
      const points_gen_children = [];

      if (checkGenericModule("motorRules", "view")) {
        points_gen_children.push({
          title: t("Gerenciar Regras"),
          path: paths.dashboard.rules.root,
        });
      }

      if (checkGenericModule("motorConversionRates", "view")) {
        points_gen_children.push({
          title: t("Taxas de Conversão"),
          path: paths.dashboard.conversionRate.root,
        });
      }

      if (checkGenericModule("motorVariables")) {
        points_gen_children.push({
          title: t("Listar Variáveis"),
          path: paths.dashboard.variables,
        });
      }

      // Só adiciona o grupo se tem pelo menos um item
      if (points_gen_children.length > 0) {
        motorItems.push({
          title: t("Geração de pontos"),
          path: "#",
          icon: ICONS.invoice,
          children: points_gen_children,
        });
      }

      // Adiciona a seção Motor se tem pelo menos um item
      if (motorItems.length > 0) {
        navSections.push({
          subheader: t("Motor de pontos"),
          items: motorItems,
        });
      }
    }

    // ===================================================================
    // SEÇÃO APP (Benefícios, Home, Banners, Usuários)
    // ===================================================================

    // 1. Subseção: Benefícios
    if (hasAppFeature()) {
      const appItems = [];
      const benefits_children = [];

      if (checkGenericModule("appProducts", "view")) {
        benefits_children.push({
          title: t("Produtos"),
          path: paths.dashboard.products.root,
        });
      }

      if (checkGenericModule("appBenefitRedeems", "view")) {
        benefits_children.push({
          title: t("Reservas"),
          path: paths.dashboard.redeems.root,
        });
      }

      if (benefits_children.length > 0) {
        appItems.push({
          title: t("Benefícios"),
          path: "#",
          icon: ICONS.label,
          children: benefits_children,
        });
      }
      if (checkGenericModule("appBanners", "view")) {
        appItems.push({
          title: t("Ofertas"),
          path: paths.dashboard.offers.root,
          icon: ICONS.product,
        });
      }

      if (checkGenericModule("appUsers", "view")) {
        appItems.push({
          title: t("Usuários"),
          path: paths.dashboard.appUsers.root,
          icon: ICONS.user,
        });
      }

      // Operadores
      const operators_children = [
        {
          title: t("Gerenciar Operadores"),
          path: paths.dashboard.sellers.root,
        },
        {
          title: t("Ranking"),
          path: paths.dashboard.ranking.root,
        },
      ];

      appItems.push({
        title: t("Operadores"),
        path: "#",
        icon: ICONS.user,
        children: operators_children,
      });

      // 9. Subseção: Documentos
      // const documents_children = [];

      // if (checkGenericModule('appTermsOfUse', 'view')) {
      //   documents_children.push({
      //     title: t('Termos de Uso'),
      //     path: paths.dashboard.terms.root,
      //   });
      // }

      // // if (checkGenericModule('appPrivacyAndPolicy', 'view')) {
      // //   documents_children.push({
      // //     title: t('Política de Privacidade'),
      // //     path: paths.dashboard.privacy.root,
      // //   });
      // // }

      // if (documents_children.length > 0) {
      //   appItems.push({
      //     title: t('Documentos'),
      //     path: '#',
      //     icon: ICONS.document,
      //     children: documents_children,
      //   });
      // }

      navSections.push({
        subheader: t("Mercado"),
        items: appItems,
      });
    }

    // ===================================================================
    // SEÇÃO CONFIGURAÇÕES GERAIS
    // ===================================================================
    // ===================================================================
    // SEÇÃO CONFIGURAÇÕES GERAIS (Administradores)
    // ===================================================================
    const generalConfigItems = [];

    // Subseção: Administradores
    const admin_children = [];

    if (checkGenericModule("adminUsers", "view")) {
      admin_children.push({
        title: t("Gerenciar Administradores"),
        path: paths.dashboard.adminUsers.root,
      });
    }

    // Só adiciona o grupo se tem pelo menos um item
    if (admin_children.length > 0) {
      generalConfigItems.push({
        title: t("Administradores"),
        path: "#",
        icon: ICONS.user,
        children: admin_children,
      });
    }

    // Adiciona a seção Configurações Gerais se tem pelo menos um item
    if (generalConfigItems.length > 0) {
      navSections.push({
        subheader: t("Configurações Gerais"),
        items: generalConfigItems,
      });
    }

    return navSections;
  }, [
    t,
    pathname,
    hasDuplicate,
    hasMotorFeature,
    hasAppFeature,
    checkGenericModule,
  ]);

  return data;
}
